import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import DeviceEventBus, { type DeviceNewReadingDetail, type DeviceType } from '@/utils/devices/DeviceEventBus';
import Fuse from 'fuse.js';
import type { DataTableRow, DataTableService, DataTableStateChangeEvent } from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';
import {
  deviceScanFailedToast,
  deviceScanMatchToast,
  deviceScanMultipleMatchesToast,
  deviceScanNoMatchToast,
  deviceScanTableNotReadyToast,
} from './DataTableDeviceToast';

/**
 * Subscribes to DeviceEventBus and handles 'focus-search' device readings by
 * finding the matching animal row and snapping the selection to it. When a
 * workflow is active, jumping to the matched animal restarts the navigation
 * flow for that animal (re-scan support).
 */
export class DataTableDeviceFocusSearch {
  readonly #abortController = new AbortController();
  readonly #dataTableService: DataTableService;
  #fuse: Fuse<DataTableRow> | undefined;

  constructor(dataTableService: DataTableService) {
    this.#dataTableService = dataTableService;

    const signal = this.#abortController.signal;

    dataTableService.subscribe(DataTableEvent.TableStateChange, this.#handleTableStateChange, signal);

    DeviceEventBus.subscribe('device:new-reading', this.#handleReading, { signal });
  }

  destroy(): void {
    this.#abortController.abort();
  }

  readonly #handleTableStateChange = (event: DataTableStateChangeEvent): void => {
    if (event.detail.type.has('rows')) {
      this.#fuse = undefined;
    }
  };

  #getFuse(): Fuse<DataTableRow> | undefined {
    if (_isNil(this.#fuse)) {
      const rows = this.#dataTableService.rows;
      if (_isNotEmpty(rows)) {
        this.#fuse = new Fuse(rows, {
          includeMatches: true,
          threshold: 0,
          ignoreLocation: true,
          ignoreFieldNorm: true,
          keys: [
            ['animal', 'name'],
            ['animal', 'alt_ids', 'donor'],
            ['animal', 'alt_ids', 'ear'],
            ['animal', 'alt_ids', 'tag'],
            ['animal', 'alt_ids', 'tail'],
          ],
        });
      }
    }
    return this.#fuse;
  }

  readonly #handleReading = (event: CustomEvent<DeviceNewReadingDetail>): void => {
    if (document.hidden) {
      return;
    }

    if (event.detail.target !== 'focus-search') {
      return;
    }

    const workflowActive = this.#dataTableService.workflowService.workflowActive;

    const trimmed = event.detail.value.trim();
    if (trimmed.length === 0) {
      return;
    }

    event.preventDefault();
    this.#processFocusSearch(trimmed, workflowActive, event.detail.device.title, event.detail.device.type);
  };

  #processFocusSearch(
    value: string,
    workflowActive: boolean,
    deviceTitle: string,
    deviceType: DeviceType | undefined
  ): void {
    try {
      const fuse = this.#getFuse();
      if (_isNil(fuse)) {
        deviceScanTableNotReadyToast(deviceTitle, deviceType);
        return;
      }

      const results = fuse.search(value);
      // Fuse only narrows candidates here; exactness is asserted against the matched
      // field value rather than the result score, whose magnitude for an exact hit is
      // an undocumented implementation detail that shifts between Fuse releases.
      const searchValue = value.toLowerCase();
      const perfectMatches = results.filter((result) =>
        (result.matches ?? []).some((match) => _notNil(match.value) && match.value.toLowerCase() === searchValue)
      );

      if (perfectMatches.length === 0) {
        deviceScanNoMatchToast(deviceTitle, value, deviceType);
        return;
      }

      if (perfectMatches.length > 1) {
        deviceScanMultipleMatchesToast(deviceTitle, value, deviceType);
        return;
      }

      const matchedRow = perfectMatches[0].item;
      const rowIndex = this.#dataTableService.toIndexRow(matchedRow.id);
      if (_isNil(rowIndex)) {
        deviceScanNoMatchToast(deviceTitle, value, deviceType);
        return;
      }

      if (workflowActive) {
        if (this.#dataTableService.workflowService.workflowActionRescan(rowIndex)) {
          const landedColumnIndex = this.#dataTableService.selection?.topLeft.column;
          const landedColumn = _notNil(landedColumnIndex)
            ? this.#dataTableService.columnByIndex(landedColumnIndex)
            : undefined;
          if (_notNil(landedColumn)) {
            this.#dataTableService.flashCell({ column: landedColumn.id, row: matchedRow.id }, 'blue');
          }
          deviceScanMatchToast(deviceTitle, value, deviceType);
        }
      } else {
        const currentColumnIndex = this.#dataTableService.selection?.topLeft.column ?? 0;
        const currentColumn = this.#dataTableService.columnByIndex(currentColumnIndex);
        this.#dataTableService.selectCell(
          { column: currentColumnIndex, row: rowIndex },
          { scrollTo: true, activate: false, source: 'api' }
        );
        if (_notNil(currentColumn)) {
          this.#dataTableService.flashCell({ column: currentColumn.id, row: matchedRow.id }, 'blue');
        }
        deviceScanMatchToast(deviceTitle, value, deviceType);
      }
    } catch (err) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to process device focus search', {
          cause: err,
          slug: 'datatable-device-focus-search-error',
          context: { value },
        })
      );
      deviceScanFailedToast(deviceTitle, deviceType);
    }
  }
}
